import { useState, useEffect, useCallback } from 'react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';

import { RequestState, RequestStateType } from '../RequestState';
import { Conversations } from '../types';
import { useMessagingClient } from './useMessagingClient';

export type { RequestState } from '../RequestState';
export { RequestStateType } from '../RequestState';

export function useConversations(): {
    conversations: Conversations;
    conversationsRequestState: RequestState;
    requestConversations: () => void;
} {
    const messagingClient = useMessagingClient();

    // LOCAL STATE

    const [conversationsRequestState, setConversationsRequestState] = useState<RequestState>({
        state: RequestStateType.LOADING,
    });

    // EXTERNAL STATE

    const conversations: ReturnType<typeof messagingClient.selectors.getConversations> =
        useSyncExternalStoreWithSelector(
            messagingClient.subscribe,
            messagingClient.getState,
            undefined,
            messagingClient.selectors.getConversations,
        );

    // API

    const getConversations = useCallback(() => {
        messagingClient
            .getConversations()
            .then(() => setConversationsRequestState({ state: RequestStateType.IDLE }))
            .catch(error => setConversationsRequestState({ state: RequestStateType.ERROR, error }));
    }, [messagingClient, setConversationsRequestState]);

    const requestConversations = useCallback(() => {
        if (conversationsRequestState.state === RequestStateType.LOADING) {
            return;
        }

        setConversationsRequestState({ state: RequestStateType.LOADING });
        getConversations();
    }, [getConversations, conversationsRequestState, setConversationsRequestState]);

    // LIFECYCLE

    // Only request conversations automatically on mount.
    useEffect(() => {
        getConversations();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        conversationsRequestState,
        conversations,
        requestConversations,
    };
}
